import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import Notification from './Notification';
import Spinner from './Spinner';
import ImageGallery from './ImageGallery';
import './ListaMayorista.css';
import useGlobalTheme from '../hooks/useGlobalTheme';
import { useTracking } from '../hooks/useTracking';
import { computeCartDiscounts } from '../utils/cartDiscounts';

const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0,00';
  }
  return number
    .toFixed(2)
    .replace('.', ',')
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Modal para ampliar imagen
const ImageModal = ({ imageUrl, productModel, productDescription, onClose }) => {
  const tooltipText = productDescription || productModel;
  return (
    <div className="lm-modal-overlay" onClick={onClose}>
      <div className="lm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="lm-modal-header">
          <h3 className="lm-modal-title" title={tooltipText}>{productModel}</h3>
          <button onClick={onClose} className="lm-close-btn">×</button>
        </div>
        <img src={imageUrl} alt={productModel} title={tooltipText} className="lm-enlarged-image" />
      </div>
    </div>
  );
};

// Devuelve las categorías del producto (una o varias) para comparaciones
const getProductCategories = (product) => {
  const cats = product.categories && Array.isArray(product.categories) ? product.categories : [];
  const single = product.category ? [product.category] : [];
  return [...new Set([...single, ...cats])];
};

// Función para obtener todos los descuentos aplicables a un producto (SOLO individuales)
const getApplicableDiscounts = (product, discountRules) => {
  // Si el producto no es apto para descuentos, no devolver ninguno
  if (product.discountExcluded) {
    return [];
  }

  const discounts = [];
  const productCategories = getProductCategories(product).map(c => (c || '').toLowerCase());
  const keyword = (rule) => (rule.keyword || '').toLowerCase();

  discountRules.forEach(rule => {
    switch (rule.type) {
      case 'keyword':
        // SOLO agregar si el producto contiene la palabra clave (modelo o alguna categoría)
        const kw = keyword(rule);
        const matchModel = product.model && product.model.toLowerCase().includes(kw);
        const matchCategory = productCategories.some(cat => cat && cat.includes(kw));
        if (matchModel || matchCategory) {
          discounts.push({
            type: 'keyword',
            percentage: rule.percentage,
            description: `Por palabra clave "${rule.keyword}"`,
            price: product.price * (1 - rule.percentage / 100)
          });
        }
        break;
      case 'quantity':
        // Los descuentos por cantidad se muestran como informativos
        // pero no se calculan aquí (dependen de la cantidad seleccionada)
        discounts.push({
          type: 'quantity',
          percentage: rule.percentage,
          description: `Por cantidad mínima (${rule.minQuantity}+)`,
          price: product.price * (1 - rule.percentage / 100),
          minQuantity: rule.minQuantity,
          informative: true // Marcar como informativo
        });
        break;
      case 'amount':
        // Los descuentos por monto se muestran como informativos
        // pero no se calculan aquí (dependen del total del carrito)
        discounts.push({
          type: 'amount',
          percentage: rule.percentage,
          description: `Por monto total (${formatNumber(rule.minAmount)}+)`,
          price: product.price * (1 - rule.percentage / 100),
          minAmount: rule.minAmount,
          informative: true // Marcar como informativo
        });
        break;
      default:
        break;
    }
  });

  // Ordenar por porcentaje de descuento (mayor primero)
  return discounts.sort((a, b) => b.percentage - a.percentage);
};

const computeCartWithDiscounts = (cart, discountRules) =>
  computeCartDiscounts(cart, discountRules);

// Componente de resumen detallado del carrito
const DetailedCartSummary = ({
  cart,
  discountRules,
  onUpdateQuantity,
  onRemoveItem,
  onClose,
  products,
  precomputedMetrics
}) => {
  const { items: cartWithDiscounts, totals, itemCount } = useMemo(() => {
    if (precomputedMetrics && precomputedMetrics.items) {
      return precomputedMetrics;
    }
    return computeCartWithDiscounts(cart, discountRules);
  }, [cart, discountRules, precomputedMetrics]);

  if (cart.length === 0 || cartWithDiscounts.length === 0) return null;

  return (
    <div className="lm-detailed-cart">
      <div className="lm-cart-header">
        <h3 className="lm-cart-title">🛒 Resumen del Carrito Mayorista</h3>
        <div className="lm-cart-header-controls">
          <div className="lm-cart-stats">
            <span className="lm-cart-items">{itemCount} productos</span>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="lm-cart-close-btn"
              title="Cerrar carrito"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      <div className="lm-cart-items-list">
        {cartWithDiscounts.map((item) => (
          <div key={item.id} className="lm-cart-item">
            <div className="lm-cart-item-info">
              <div className="lm-cart-item-name">{item.model}</div>
              <div className="lm-cart-item-category">
                {item.categories?.length > 1 ? item.categories.join(', ') : (item.category || item.categories?.[0] || '')}
              </div>
            </div>

            <div className="lm-cart-item-quantity">
              <button
                onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                disabled={item.quantity <= 1}
                className="lm-cart-qty-btn"
              >
                -
              </button>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) => {
                  const p = products.find(pr => pr.id === item.id);
                  const maxStock = p?.excludeFromStock ? 999 : (p?.stock ?? item.stock ?? 999);
                  const newValue = parseInt(e.target.value) || 1;
                  const validQuantity = Math.max(1, Math.min(newValue, maxStock));
                  onUpdateQuantity(item.id, validQuantity);
                }}
                min="1"
                max={products.find(p => p.id === item.id)?.excludeFromStock ? 999 : (products.find(p => p.id === item.id)?.stock || item.stock || 999)}
                className="lm-cart-qty-input"
              />
              <button
                onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                disabled={!products.find(p => p.id === item.id)?.excludeFromStock && item.quantity >= (products.find(p => p.id === item.id)?.stock || item.stock || 999)}
                className="lm-cart-qty-btn"
              >
                +
              </button>
            </div>

            <div className="lm-cart-item-prices">
              {item.bestDiscount.percentage > 0 ? (
                <>
                  <div className="lm-cart-original-price">
                    <span className="lm-cart-price-label">Original:</span>
                    <span className="lm-cart-price-value">${formatNumber(item.originalTotal)}</span>
                  </div>
                  <div className="lm-cart-final-price-row">
                    <span className="lm-cart-price-label">Con descuento:</span>
                    <span className="lm-cart-final-price">${formatNumber(item.discountedTotal)}</span>
                  </div>
                </>
              ) : (
                <div className="lm-cart-no-discount">
                  <span className="lm-cart-price-label">Total:</span>
                  <span className="lm-cart-final-price">${formatNumber(item.discountedTotal)}</span>
                </div>
              )}
            </div>

            <button
              onClick={() => onRemoveItem(item.id)}
              className="lm-cart-remove-btn"
              title="Eliminar producto"
            >
              🗑️
            </button>
          </div>
        ))}
      </div>

      <div className="lm-cart-totals">
        <div className="lm-cart-total-row lm-cart-subtotal">
          <span className="lm-cart-total-label">Subtotal original:</span>
          <span className="lm-cart-total-value lm-cart-original">${formatNumber(totals.original)}</span>
        </div>

        {totals.savings > 0 && (
          <div className="lm-cart-total-row lm-cart-savings-row">
            <span className="lm-cart-total-label">Total de descuentos ({totals.discountPercentage.toFixed(1)}%):</span>
            <span className="lm-cart-total-value lm-cart-savings-total">-${formatNumber(totals.savings)}</span>
          </div>
        )}

        <Link
          to="/carrito"
          className="lm-cart-total-row lm-cart-final-total lm-cart-final-link"
        >
          <span className="lm-cart-total-label">Total a pagar:</span>
          <span className="lm-cart-total-value lm-cart-final">${formatNumber(totals.discounted)}</span>
          <span className="lm-cart-final-link-cta">Continuar →</span>
        </Link>
      </div>
    </div>
  );
};

const resolveProductImageUrl = async (storage, imagePath) => {
  if (!imagePath) return '';
  if (typeof imagePath === 'string' && /^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }
  try {
    return await getDownloadURL(ref(storage, imagePath));
  } catch (error) {
    console.error('Error loading image:', error);
    return typeof imagePath === 'string' ? imagePath : '';
  }
};

const WholesaleProductItem = ({ product, onAddToCart, cartItems, discountRules }) => {
  const [quantity, setQuantity] = useState(1);
  const [imageUrls, setImageUrls] = useState([]);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchImageUrls = async () => {
      if (!product.images || product.images.length === 0) {
        if (!cancelled) setImageUrls([]);
        return;
      }
      const storage = getStorage();
      const urls = await Promise.all(
        product.images.map((imagePath) => resolveProductImageUrl(storage, imagePath))
      );
      if (!cancelled) {
        setImageUrls(urls.filter(Boolean));
      }
    };
    fetchImageUrls();
    return () => {
      cancelled = true;
    };
  }, [product.images]);

  const imageUrl = imageUrls[0] || '';

  const originalPrice = product.price;
  const isDiscountExcluded = product.discountExcluded;
  const allDiscounts = useMemo(
    () => (isDiscountExcluded ? [] : getApplicableDiscounts(product, discountRules)),
    [product, discountRules, isDiscountExcluded]
  );

  const applicableDiscounts = useMemo(
    () => allDiscounts.filter(discount => {
      if (discount.type === 'keyword') {
        return true; // Los descuentos por palabra clave siempre aplican si ya pasaron el filtro
      }
      if (discount.type === 'quantity') {
        return quantity >= discount.minQuantity; // Solo si cumple la cantidad mínima
      }
      if (discount.type === 'amount') {
        return false; // Los descuentos por monto se calculan en el carrito, no aquí
      }
      return false;
    }),
    [allDiscounts, quantity]
  );

  const informativeDiscounts = useMemo(
    () => allDiscounts.filter(discount => {
      if (discount.type === 'quantity') {
        return quantity < discount.minQuantity; // Mostrar como informativos si no cumple la cantidad
      }
      if (discount.type === 'amount') {
        return true; // Siempre mostrar como informativos
      }
      return false;
    }),
    [allDiscounts, quantity]
  );

  const displayableApplicableDiscounts = useMemo(
    () => applicableDiscounts.filter(discount => discount.type !== 'keyword'),
    [applicableDiscounts]
  );
  const displayableInformativeDiscounts = useMemo(
    () => informativeDiscounts.filter(discount => discount.type !== 'keyword'),
    [informativeDiscounts]
  );
  const bestDisplayableDiscount = useMemo(
    () => displayableApplicableDiscounts[0] || null,
    [displayableApplicableDiscounts]
  );

  const hasVisibleDiscount = !isDiscountExcluded && (
    !!bestDisplayableDiscount ||
    displayableApplicableDiscounts.length > 0 ||
    displayableInformativeDiscounts.length > 0
  );

  const maxQty = product.excludeFromStock ? 999 : product.stock;
  const handleQuantityChange = (value) => {
    const newQuantity = Math.max(1, Math.min(value, maxQty));
    setQuantity(newQuantity);
  };

  const handleAddToCart = () => {
    if (quantity > 0) {
      // Enviar al carrito con precio original (sin descuento)
      onAddToCart(product, quantity, originalPrice);
      setQuantity(1);
    }
  };

  // Verificar si el producto ya está en el carrito
  const cartItem = cartItems.find(item => item.id === product.id);
  const cartQuantity = cartItem ? cartItem.quantity : 0;

  return (
    <>
      <tr className="lm-table-row">
        <td className="lm-table-cell lm-image-cell" data-label="Imagen">
          {imageUrl && (
            <div
              className="lm-gallery-click-wrap"
              onClick={() => setShowImageModal(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowImageModal(true);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`Ver imagen ampliada: ${product.model}`}
            >
              <ImageGallery
                images={imageUrls}
                alt={product.model}
                imagePositions={product.imagePositions}
                className="lm-list-gallery"
                description={product.description}
                cycleImagesOnHover
              />
            </div>
          )}
        </td>
        <td className="lm-table-cell lm-info-cell" data-label="Producto">
          <div className="lm-product-info-stack">
            <Link to={`/producto/${product.id}`} className="lm-product-model" title="Ir al producto">
              {product.model}
            </Link>
            <div className="lm-product-category">
              {product.categories?.length > 1 ? product.categories.join(', ') : (product.category || product.categories?.[0] || '')}
            </div>
            <div className="lm-product-stock">
              {product.excludeFromStock ? 'Disponible' : `Stock: ${product.stock}`}
            </div>
            {!product.excludeFromStock && product.stock <= 5 && product.stock > 0 && (
              <div className="lm-stock-warning" style={{ color: '#f59e0b', fontWeight: 600 }}>
                ⚠️ Stock bajo
              </div>
            )}
            {cartQuantity > 0 && (
              <div className="lm-cart-indicator">En carrito: {cartQuantity}</div>
            )}
          </div>
        </td>
        <td className="lm-table-cell lm-price-cell" data-label="Precios">
          {/* Precio público */}
          <div className={`lm-public-price${hasVisibleDiscount ? ' lm-public-price--discounted' : ''}`}>
            <span className="lm-price-label">
              {hasVisibleDiscount ? '🏷️ Precio original:' : '🏷️ Precio:'}
            </span>
            <span className={`lm-price-value${hasVisibleDiscount ? ' lm-price-value--discounted' : ''}`}>
              ${formatNumber(originalPrice)}
            </span>
          </div>

          {!isDiscountExcluded && bestDisplayableDiscount && (
            <div className="lm-best-discount">
              <span className="lm-best-discount-title">Mejor descuento disponible</span>
              <span className="lm-best-discount-amount">
                {bestDisplayableDiscount.description}: ${formatNumber(bestDisplayableDiscount.price)}
                <span className="lm-discount-badge-inline">-{bestDisplayableDiscount.percentage}%</span>
              </span>
            </div>
          )}

          {/* Precios con descuentos que REALMENTE aplican */}
          {!isDiscountExcluded && displayableApplicableDiscounts.length > 0 && (
            <div className="lm-discount-prices">
              <div className="lm-discount-label">Precios mayoristas aplicables:</div>
              {displayableApplicableDiscounts.map((discount, index) => (
                <div key={index} className={`lm-discount-price lm-discount-${discount.type}`}>
                  {/* <span className="lm-discount-description">{discount.description}:</span> */}
                  <span className="lm-discount-value">
                    ${formatNumber(discount.price)}
                    <span className="lm-discount-badge-inline">-{discount.percentage}%</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Descuentos informativos (que podrían aplicar) */}
          {!isDiscountExcluded && displayableInformativeDiscounts.length > 0 && (
            <div className="lm-discount-prices lm-discount-prices--spaced">
              <div className="lm-discount-label lm-discount-label--small">Descuentos disponibles:</div>
              {displayableInformativeDiscounts.map((discount, index) => (
                <div key={index} className={`lm-discount-price lm-discount-${discount.type}`} >
                  <span className="lm-discount-badge-inline">-{discount.percentage}%</span>
                  <span className="lm-discount-value">
                    ${formatNumber(discount.price)}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Sin descuentos o producto no apto */}
          {isDiscountExcluded ? (
            <div className="lm-no-discounts">
              <span>Este producto no aplica para descuento</span>
            </div>
          ) : (
            displayableApplicableDiscounts.length === 0 && displayableInformativeDiscounts.length === 0 && (
              <div className="lm-no-discounts">
                <span>Sin descuentos disponibles</span>
              </div>
            )
          )}
        </td>
        <td className="lm-table-cell lm-quantity-cell" data-label="Cantidad">
          <div className="lm-quantity-cell-inner">
            <div className="lm-quantity-controls">
              <button
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
                className="lm-quantity-btn"
              >
                -
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                min="1"
                max={maxQty}
                className="lm-quantity-input"
              />
              <button
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={quantity >= maxQty}
                className="lm-quantity-btn"
              >
                +
              </button>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={quantity === 0 || (!product.excludeFromStock && product.stock === 0)}
              className="lm-add-btn"
              title={(!product.excludeFromStock && product.stock === 0) ? 'Sin stock disponible' : 'Agregar al carrito'}
            >
              Agregar
            </button>
          </div>
        </td>
      </tr>

      {showImageModal && (
        <ImageModal
          imageUrl={imageUrl}
          productModel={product.model}
          productDescription={product.description}
          onClose={() => setShowImageModal(false)}
        />
      )}
    </>
  );
};

const ListaMayorista = ({ cart, setCart }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ message: '', show: false });
  const [discountRules, setDiscountRules] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { isDarkMode } = useGlobalTheme();

  // Calcular totales del carrito para el botón
  const cartMetrics = useMemo(
    () => computeCartWithDiscounts(cart, discountRules),
    [cart, discountRules]
  );
  const cartItemsCount = cartMetrics.itemCount;
  const cartTotal = cartMetrics.totals.discounted || cart.reduce((total, item) => total + (item.price * item.quantity), 0);

  // Función para mostrar notificaciones
  const showNotification = (message, type = 'success') => {
    setNotification({ message, show: true, type });
    setTimeout(() => {
      setNotification({ message: '', show: false });
    }, 3000);
  };

  // Cargar reglas de descuento desde Firebase
  const loadDiscountRules = useCallback(async () => {
    try {
      const discountsCollection = await getDocs(collection(db, 'discounts'));
      const discountsData = discountsCollection.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDiscountRules(discountsData);
    } catch (error) {
      console.error('Error loading discount rules:', error);
      showNotification('Error al cargar los descuentos.', 'error');
    }
  }, []);

  // Obtener productos de Firebase
  const fetchProducts = useCallback(async () => {
    try {
      const productsCollection = await getDocs(collection(db, 'products'));
      const productsData = productsCollection.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        excludeFromStock: doc.data().excludeFromStock ?? false
      })).filter(product => !product.hidden && (product.stock > 0 || product.excludeFromStock === true));

      setProducts(productsData);

      const fromCategory = productsData.map(p => p.category).filter(Boolean);
      const fromCategories = productsData.flatMap(p => Array.isArray(p.categories) ? p.categories : []);
      const uniqueCategories = [...new Set([...fromCategory, ...fromCategories])];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error("Error fetching products:", error);
      showNotification('Error al cargar productos.', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    loadDiscountRules();
  }, [fetchProducts, loadDiscountRules]);

  const { trackLead } = useTracking();

  // Agregar al carrito con precio original (sin descuento)
  const addToCart = (product, quantity, originalPrice) => {
    const existingProduct = cart.find(item => item.id === product.id);

    if (existingProduct) {
      const updatedCart = cart.map(item =>
        item.id === product.id
          ? {
            ...item,
            quantity: item.quantity + quantity,
            price: originalPrice
          }
          : item
      );
      setCart(updatedCart);
    } else {
      const newCartItem = {
        ...product,
        quantity,
        price: originalPrice
      };
      setCart([...cart, newCartItem]);
    }

    showNotification(`Agregado ${quantity} ${product.model} al carrito.`, 'success');
    trackLead(); // Considera mover esto a un lugar más adecuado si tienes un formulario de contacto
  };

  // Actualizar cantidad en el carrito
  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    const updatedCart = cart.map(item =>
      item.id === productId
        ? { ...item, quantity: newQuantity }
        : item
    );
    setCart(updatedCart);

  };

  // Eliminar producto del carrito
  const removeFromCart = (productId) => {
    const updatedCart = cart.filter(item => item.id !== productId);
    setCart(updatedCart);

  };

  // Filtrar productos (incluye productos que tengan la categoría en category o en categories)
  const filteredProducts = useMemo(
    () =>
      products
        .filter(product => {
          if (selectedCategory === 'all') return true;
          if (product.category === selectedCategory) return true;
          return product.categories && product.categories.includes(selectedCategory);
        })
        .sort((a, b) => a.model.localeCompare(b.model)),
    [products, selectedCategory]
  );

  if (loading) return <Spinner />;

  // Función para imprimir la lista de productos
  const handlePrint = async () => {
    // Obtener URLs de imágenes para todos los productos
    const getImageUrl = async (product) => {
      if (product.images && product.images.length > 0) {
        try {
          const storage = getStorage();
          const imageRef = ref(storage, product.images[0]);
          return await getDownloadURL(imageRef);
        } catch {
          return '';
        }
      }
      return '';
    };

    // Prepara los productos con sus URLs de imagen
    const productsWithImages = await Promise.all(
      filteredProducts.map(async (product) => ({
        ...product,
        imageUrl: await getImageUrl(product)
      }))
    );

    // Usar solo los discountRules de tipo 'amount' y 'quantity' para las columnas de descuentos
    const discountColumns = discountRules.filter(rule => rule.type !== 'keyword').map(rule => {
      let desc = '';
      if (rule.type === 'quantity') desc = `📦 Cantidad ${rule.minQuantity}+`;
      if (rule.type === 'amount') desc = `💰 Monto $${formatNumber(rule.minAmount)}+`;
      return {
        ...rule,
        desc,
        header: `${desc}<br/>(-${rule.percentage}%)`
      };
    });

    // Generar encabezado dinámico
    const tableHeaders = [
      '<th>Imagen</th>',
      '<th>Producto</th>',
      '<th>Precio original</th>',
      ...discountColumns.map(d => `<th>${d.header}</th>`)
    ].join('');

    const productCats = (p) => (p.categories?.length ? p.categories : (p.category ? [p.category] : [])).join(', ');
    const productCatsLower = (p) => getProductCategories(p).map(c => c.toLowerCase());

    // Generar filas
    const tableRows = productsWithImages.map(product => {
      const catsLower = productCatsLower(product);
      const keywordMatch = (kw) => product.model?.toLowerCase().includes(kw) || catsLower.some(c => c && c.includes(kw));
      return `
        <tr>
          <td><img src="${product.imageUrl}" alt="${product.model}" /></td>
          <td><b>${product.model}</b><div style="font-size:0.95em; color:#555;">${productCats(product)}</div></td>
          <td>$${formatNumber(product.price)}</td>
          ${product.discountExcluded
            ? discountColumns.map(() => '<td></td>').join('')
            : discountColumns.map(rule => {
                let applies = false;
                if (rule.type === 'keyword') {
                  applies = rule.keyword && keywordMatch((rule.keyword || '').toLowerCase());
                } else if (rule.type === 'quantity') {
                  // No se puede saber la cantidad desde aquí, así que siempre mostramos el precio con descuento
                  applies = true;
                } else if (rule.type === 'amount') {
                  // No se puede saber el monto del carrito aquí, así que siempre mostramos el precio con descuento
                  applies = true;
                }
                if (applies) {
                  const priceWithDiscount = product.price * (1 - rule.percentage / 100);
                  return `<td class="lm-print-price-discount"><b>$${formatNumber(priceWithDiscount)}</b></td>`;
                } else {
                  return '<td></td>';
                }
              }).join('')}
        </tr>
      `;
    }).join('');

    // Generar HTML imprimible
    const style = `
      <style>
        body { font-family: Arial, sans-serif; color: #222; }
        h1 { text-align: center; }
        table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
        th, td { border: 1px solid #aaa; padding: 8px; text-align: left; }
        th { background: #f0f0f0; }
        img { max-width: 80px; max-height: 80px; display: block; margin: 0 auto; }
        .lm-print-price-discount { color: #22c55e; }
      </style>
    `;
    const html = `
      <html>
        <head>
          <title>Lista Mayorista - Imprimir</title>
          ${style}
        </head>
        <body>
          <h1>Lista de Precios Mayorista</h1>
          <table>
            <thead>
              <tr>${tableHeaders}</tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  return (
    <div className={`lm-container ${isDarkMode ? 'lm-dark-theme' : 'lm-light-theme'}`}>
      {notification.show && (
        <Notification
          message={notification.message}
          show={notification.show}
          type={notification.type}
        />
      )}

      <div className="lm-header">
        <div className="lm-title-section">
          <h1 className="lm-title">Lista de Precios Mayorista</h1>
          <div className="lm-header-controls">
            {/* Botón de imprimir */}
            <button
              onClick={handlePrint}
              className="lm-print-btn lm-btn lm-btn-print"
              title="Imprimir lista de productos"
            >
              🖨️ Imprimir
            </button>
          </div>
        </div>

        {discountRules.length > 0 && (
          <div className="lm-discount-panel">
            <h3 className="lm-discount-title">Descuentos Mayoristas Disponibles</h3>
            <div className="lm-discount-grid">
              {discountRules
                .filter(rule => rule.type !== 'keyword')
                .map((rule, index) => (
                  <div key={index} className="lm-discount-item">
                    {rule.type === 'quantity' && (
                      <span>📦 Cantidad {rule.minQuantity}+: {rule.percentage}% descuento</span>
                    )}
                    {rule.type === 'amount' && (
                      <span>💰 Monto ${formatNumber(rule.minAmount)}+: {rule.percentage}% descuento</span>
                    )}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Botón flotante del carrito */}
      {cart.length > 0 && (
        <div className="lm-cart-float-container">
          <button
            onClick={() => setIsCartOpen(!isCartOpen)}
            className={`lm-cart-float-btn ${isCartOpen ? 'lm-cart-open' : ''}`}
          >
            <div className="lm-cart-icon">
              🛒
              <span className="lm-cart-badge">{cartItemsCount}</span>
            </div>
            <div className="lm-cart-info">
              <span className="lm-cart-label">Carrito</span>
              <span className="lm-cart-total">${formatNumber(cartTotal)}</span>
              {cartMetrics.totals.savings > 0 && (
                <span className="lm-cart-savings">
                  Ahorro: ${formatNumber(cartMetrics.totals.savings)}
                </span>
              )}
            </div>
            <div className="lm-cart-arrow">
              {isCartOpen ? '▼' : '▲'}
            </div>
          </button>
        </div>
      )}

      {/* Carrito desplegable */}
      {isCartOpen && (
        <div className="lm-cart-overlay" onClick={() => setIsCartOpen(false)}>
          <div className="lm-cart-dropdown" onClick={(e) => e.stopPropagation()}>
            <DetailedCartSummary
              cart={cart}
              discountRules={discountRules}
              onUpdateQuantity={updateCartQuantity}
              onRemoveItem={removeFromCart}
              onClose={() => setIsCartOpen(false)}
              products={products}
              precomputedMetrics={cartMetrics}
            />
          </div>
        </div>
      )}

      <div className="lm-category-filter">
        <label className="lm-filter-label">Filtrar por categoría:</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="lm-category-select"
        >
          <option value="all">Todas las categorías</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      <div className="lm-table-container">
        <table className="lm-table">
          <thead className="lm-table-header">
            <tr>
              <th>Imagen</th>
              <th>Producto</th>
              <th>Precios</th>
              <th>Cantidad</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => (
              <WholesaleProductItem
                key={product.id}
                product={product}
                onAddToCart={addToCart}
                cartItems={cart}
                discountRules={discountRules}
              />
            ))}
          </tbody>
        </table>
      </div>

      {filteredProducts.length === 0 && (
        <div className="lm-no-products">
          No hay productos disponibles en esta categoría.
        </div>
      )}
    </div>
  );
};

export default ListaMayorista;